// src/lib/map/whenMapLoaded.js
//
// Run once a MapLibre map has loaded, even when the frame that would fire
// MapLibre's own `load` never comes (REM-704).
//
// MapLibre fires `load` inside a drawn frame (_render), the first time
// loaded() is true there. A tile that fails with anything but a 404 is marked
// errored and the source fires `error`, and nothing asks for a frame. When
// that tile was the last one pending, loaded() is now true and no frame
// follows: `load` never fires, for this caller or anyone else (Explore's
// compare map came up with no slider, no footer and no imagery, 1 in 48 HUD
// entries; every time with the app's tiles answered 500 after Mapbox's).
//
// So on an error, if the map has loaded, ask for the frame: MapLibre then
// fires its own `load`, once, for every listener, and this runs from it.
//
// The one readiness helper (S-e slice 4a, DB 2026-11-03): every map that
// waited on "load" for its setup waits here. It runs the FIRST time loaded()
// is true on load, idle or render, exactly once. While MapLibre's own load
// has not fired (map._loaded false), a render with loaded() true is the frame
// that load is about to fire in, so it is left to load: a setup run there that
// adds a source turns loaded() false and postpones load past that frame, and
// every secondary map.once("load") wait with it (seen on the main map's HUD
// run). render and idle are for a wait put on after load fired, while tiles
// were pending.
//
// The repaint on error outlives the run, until the map is removed or the wait
// cancelled: after the first errors MapLibre asks for parent tiles, and when
// the last of those fails there is again no frame, so a setup's own
// map.once("idle") never fired (the main map with every Mapbox tile answered
// 500, 2 runs in 3 in the HUD).
//
// Not covered: a 404 (MapLibre looks for parent tiles instead and fires
// nothing) ending a load with nothing else pending. Not seen.
//
// @returns {() => void} cancel: never runs after it, and lets go of the map
import { isLiveMap } from "./liveMap";

// Strictly true, as MapLibre answers: a stand-in without loaded(), or one
// answering anything with a stub (the component tests' maps, which also call
// their load listeners with no event), counts as not loaded and waits on load
// alone, as map.on("load") did.
const isLoaded = (map) => typeof map.loaded === "function" && map.loaded() === true;

export function whenMapLoaded(map, run) {
  if (!isLiveMap(map)) return () => {};
  let ran = false;
  let released = false;
  // One handler per event, so load does not depend on the event it is given.
  const handlers = {
    load: () => ready(),
    idle: () => { if (isLoaded(map)) ready(); },
    render: () => { if (isLoaded(map) && map._loaded === true) ready(); },
  };
  const stopWaiting = () => {
    ran = true;
    for (const [t, fn] of Object.entries(handlers)) map.off(t, fn);
  };
  const release = () => {
    stopWaiting();
    released = true;
    map.off("error", onError);
    map.off("remove", release);
  };
  // stopWaiting() before run(): a run that throws is still the one run.
  function ready() {
    if (ran || !isLiveMap(map)) return;
    stopWaiting();
    run(map);
  }
  function onError() {
    if (released || !isLiveMap(map)) return;
    if (isLoaded(map)) map.triggerRepaint();
  }
  map.on("error", onError);
  map.on("remove", release);
  if (isLoaded(map)) {
    stopWaiting();
    run(map);
  } else {
    for (const [t, fn] of Object.entries(handlers)) map.on(t, fn);
  }
  return release;
}
